import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
  Shield as CommanderIcon,
  Person as HeadcounterIcon,
} from '@mui/icons-material';
import { AssignmentContact } from '../../Types/Incident';
import { appColors } from '../../Config/Theme';

const MAX_COMPACT_NAMES = 3;

interface HeadcountTeamSummaryProps {
  incidentCommanders: AssignmentContact[];
  headcounters: AssignmentContact[];
  variant?: 'compact' | 'detailed';
}

const getDisplayName = (c: AssignmentContact): string =>
  c.displayName || c.email || 'Unknown';

const formatCompactList = (contacts: AssignmentContact[]): string => {
  if (contacts.length === 0) return '';
  const names = contacts.map(getDisplayName);
  if (names.length <= MAX_COMPACT_NAMES) return names.join(', ');
  return `${names.slice(0, MAX_COMPACT_NAMES - 1).join(', ')} +${names.length - (MAX_COMPACT_NAMES - 1)} more`;
};

const formatTooltip = (c: AssignmentContact): string => {
  const parts: string[] = [];
  if (c.email) parts.push(c.email);
  if (c.phone) parts.push(c.phone);
  return parts.length > 0 ? parts.join(' • ') : '';
};

/* ── Compact variant (Incident List cards) ── */
const CompactView: React.FC<HeadcountTeamSummaryProps> = ({ incidentCommanders, headcounters }) => {
  const hasAny = incidentCommanders.length > 0 || headcounters.length > 0;

  if (!hasAny) {
    return (
      <Typography variant="caption" sx={{ color: appColors.text.placeholder, fontStyle: 'italic' }}>
        Headcount team not assigned
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {incidentCommanders.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption }}>
            Incident Commander:
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: appColors.text.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={incidentCommanders.map(getDisplayName).join(', ')}
          >
            {formatCompactList(incidentCommanders)}
          </Typography>
        </Box>
      )}
      {headcounters.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption }}>
            Headcounter:
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: appColors.text.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={headcounters.map(getDisplayName).join(', ')}
          >
            {formatCompactList(headcounters)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/* ── Contact row for detailed variant ── */
const ContactRow: React.FC<{ contact: AssignmentContact }> = ({ contact }) => {
  const name = getDisplayName(contact);
  const detail = formatTooltip(contact);

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, lineHeight: 1.3 }}>
        {name}
      </Typography>
      {detail && (
        <Typography variant="caption" sx={{ color: appColors.text.caption, lineHeight: 1.3 }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
};

/* ── Detailed variant (Dashboard) ── */
const DetailedView: React.FC<HeadcountTeamSummaryProps> = ({ incidentCommanders, headcounters }) => (
  <Box
    sx={{
      bgcolor: appColors.surface.card,
      borderRadius: '12px',
      border: `1px solid ${appColors.border.default}`,
      px: 3,
      py: 2,
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1.5 }}>
      Headcount Team
    </Typography>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
      }}
    >
      {/* Commanders */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <CommanderIcon sx={{ fontSize: 16, color: appColors.text.caption }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
            Incident Commander(s)
          </Typography>
        </Box>
        {incidentCommanders.length === 0 ? (
          <Typography variant="body2" sx={{ color: appColors.text.placeholder, fontStyle: 'italic' }}>
            No incident commanders assigned
          </Typography>
        ) : (
          incidentCommanders.map((c, i) => <ContactRow key={c.id ?? i} contact={c} />)
        )}
      </Box>

      {/* Headcounters */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <HeadcounterIcon sx={{ fontSize: 16, color: appColors.text.caption }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
            Headcounter(s)
          </Typography>
        </Box>
        {headcounters.length === 0 ? (
          <Typography variant="body2" sx={{ color: appColors.text.placeholder, fontStyle: 'italic' }}>
            No headcounters assigned
          </Typography>
        ) : (
          headcounters.map((c, i) => <ContactRow key={c.id ?? i} contact={c} />)
        )}
      </Box>
    </Box>
  </Box>
);

const HeadcountTeamSummary: React.FC<HeadcountTeamSummaryProps> = (props) => {
  if (props.variant === 'detailed') return <DetailedView {...props} />;
  return <CompactView {...props} />;
};

export default HeadcountTeamSummary;
